import { updateMaintenanceSetting } from "@/api/services/admin/system-setting";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { InputDateTimePicker } from "@/components/elements/InputDateTimePicker";
import { Select } from "@/components/elements/Select";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { Textarea } from "@/components/elements/Textarea";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { maintenanceSchema } from "@/features/system-settings/schema/maintenanceSchema";
import { maintenanceFormInitValues, TMaintenanceForm } from "@/features/system-settings/types/maintenanceForm";
import { convertMaintenanceSettingFormToUpdateReq } from "@/features/system-settings/utils/converts";
import { useToast } from "@/hooks/useToast";
import { orange, text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

type TMaintenanceFormProps = {
  readonly?: boolean;
  previousData?: Partial<TMaintenanceForm>;
};

export const MaintenanceForm: FC<TMaintenanceFormProps> = ({ readonly = false, previousData }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const methods = useForm<TMaintenanceForm>({
    defaultValues: { ...maintenanceFormInitValues, ...previousData },
    resolver: yupResolver(maintenanceSchema),
    mode: "onBlur",
  });

  const { control, clearErrors } = methods;

  const modeStartDateTimeWatch = useWatch({ control, name: "modeStartDateTime" });
  const modeEndDateTimeWatch = useWatch({ control, name: "modeEndDateTime" });

  useEffect(() => {
    clearErrors("modeEndDateTime");
  }, [modeStartDateTimeWatch]);

  useEffect(() => {
    clearErrors("modeStartDateTime");
  }, [modeEndDateTimeWatch]);

  const onSubmit = async (data: TMaintenanceForm) => {
    try {
      const payload = convertMaintenanceSettingFormToUpdateReq(data);
      const rs = await updateMaintenanceSetting(payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      if (rs.data.warning) {
        toast.info(rs.data.warning);
      }
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SYSTEM_SETTING.FETCH_SYSTEM_DETAIL],
      });
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form methods={methods} schema={maintenanceSchema} onSubmit={onSubmit}>
      <InnerBox padding="24px" bordered sx={{ borderRadius: "0 0 8px 8px" }}>
        <Stack gap={1}>
          <Typography variant="sub16Semi" color={text.primary}>
            メンテナンスツール
          </Typography>
          <Typography variant="body14" color={orange[80]}>
            日時を設定後、切替日のAM9:00に重要なお知らせとして画面上部に表示されます。
            <br />
            このお知らせはメールが自動配信されないため、緊急メンテナンス時は事前にお知らせしてください。
          </Typography>
          <Grid container spacing={3} maxWidth={814} mt={1}>
            <Grid item xs={6}>
              <FormField control={control} name="maintenanceMode">
                <Select readonly={readonly} enumName="EMaintenanceMode" />
              </FormField>
            </Grid>
            <Grid item xs={6} />
            <Grid item xs={6}>
              <FormField control={control} name="modeStartDateTime">
                <InputDateTimePicker readonly={readonly} timezone="Asia/Tokyo" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="modeEndDateTime">
                <InputDateTimePicker readonly={readonly} timezone="Asia/Tokyo" />
              </FormField>
            </Grid>
            <Grid item xs={12}>
              <FormField control={control} name="maintenanceMessage">
                <Textarea readonly={readonly} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
      </InnerBox>

      {!readonly && (
        <StickyFooter mt={4} justifyContent="flex-end">
          <Button size="sm" type="submit">
            設定
          </Button>
        </StickyFooter>
      )}
    </Form>
  );
};
